// Language switcher. Default: two-pill EN / اردو toggle (used app-wide,
// unchanged). `variant="pill"` renders a single compact "EN ⌄" dropdown-
// style pill (patron dashboard header, matches the redesign mockup) — since
// there are only 2 languages, a click simply toggles to the other one.
// `variant="score"` renders a sleek sliding-highlight segmented control
// (oodel SCORE public feedback form only) matching that page's minimal
// green-accent aesthetic.
import { CaretDown } from "@phosphor-icons/react";
import { useLanguage } from "../hooks/useLanguage";

export default function LanguageToggle({ className = "", variant = "default" }) {
  const { lang, setLang, featureEnabled } = useLanguage();
  if (!featureEnabled) return null;

  if (variant === "score") {
    return (
      <div
        role="group" aria-label="Language"
        className={`relative inline-flex rounded-full bg-neutral-100 p-1 select-none ${className}`}
        data-testid="lang-toggle-score"
      >
        <span
          aria-hidden="true"
          className="absolute top-1 bottom-1 left-1 rounded-full bg-mango shadow-sm transition-transform duration-300 ease-out"
          style={{ width: 52, transform: `translateX(${lang === "en" ? 0 : 52}px)` }}
        />
        <button
          type="button" onClick={() => setLang("en")} aria-pressed={lang === "en"} data-testid="lang-toggle-score-en"
          className={`relative z-10 w-[52px] py-1.5 text-xs font-bold rounded-full transition-colors duration-300 ${lang === "en" ? "text-white" : "text-neutral-500 hover:text-neutral-700"}`}
        >
          EN
        </button>
        <button
          type="button" onClick={() => setLang("ur")} aria-pressed={lang === "ur"} data-testid="lang-toggle-score-ur"
          style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif" }}
          className={`relative z-10 w-[52px] py-1.5 text-sm font-bold rounded-full transition-colors duration-300 ${lang === "ur" ? "text-white" : "text-neutral-500 hover:text-neutral-700"}`}
        >
          اردو
        </button>
      </div>
    );
  }

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={() => setLang(lang === "en" ? "ur" : "en")}
        aria-label="Change language"
        data-testid="lang-toggle-pill"
        className={`inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors ${className}`}
      >
        <span style={lang === "ur" ? { fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif" } : undefined}>{lang === "en" ? "EN" : "اردو"}</span>
        <CaretDown size={11} weight="bold" className="text-neutral-400" />
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Language"
      className={`inline-flex items-center border border-black rounded-full overflow-hidden select-none ${className}`}
      data-testid="lang-toggle"
    >
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        data-testid="lang-toggle-en"
        className={`px-3 py-1 text-xs font-semibold tracking-wide transition-colors ${
          lang === "en"
            ? "bg-black text-white"
            : "bg-transparent text-black hover:bg-black/5"
        }`}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLang("ur")}
        aria-pressed={lang === "ur"}
        data-testid="lang-toggle-ur"
        className={`px-3 py-1 text-sm font-semibold transition-colors ${
          lang === "ur"
            ? "bg-black text-white"
            : "bg-transparent text-black hover:bg-black/5"
        }`}
        // Force Naskh font on the label itself so the pill looks native
        // even before the global font swap runs.
        style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif" }}
      >
        اردو
      </button>
    </div>
  );
}
